import { useEffect, useState } from "react";

import { PageHead } from "./Shell";
import { AdminError, admin } from "./api";

/**
 * Dokumentacja API w panelu.
 *
 * Leżała w repozytorium, czyli tam, gdzie nie zagląda ten, kto jej potrzebuje:
 * integrator dostaje klucz do panelu, nie dostęp do gita. Treść jest ta sama —
 * `docs/API.md` czytany przez serwer — a nie druga kopia pisana w komponencie,
 * bo kopia rozjeżdża się z oryginałem przy pierwszej zmianie.
 */
export function Docs() {
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    admin
      .docs()
      .then((data) => setMarkdown(data.markdown))
      .catch((caught) => setError((caught as AdminError).message));
  }, []);

  return (
    <section className="adm__section">
      <PageHead
        title="Dokumentacja API"
        subtitle="Metody, których używa system zlecający — ta sama treść, co w repozytorium"
      />
      {error && <div className="adm__alert adm__alert--error">{error}</div>}
      {!markdown && !error && <div className="spinner" />}
      {markdown && <article className="doc">{render(markdown)}</article>}
    </section>
  );
}

/**
 * Minimalny markdown: nagłówki, akapity, listy, tabele, bloki kodu i kreski.
 *
 * Świadomie bez biblioteki — dokumentacja jest nasza, więc zbiór użytych
 * konstrukcji jest znany i skończony. Pełny parser zaciągnąłby kilkadziesiąt
 * kilobajtów po to, żeby obsłużyć składnię, której w tym pliku nie ma.
 */
function render(source: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const lines = source.split("\n");
  let position = 0;

  const push = (node: React.ReactNode) => out.push(<div key={out.length}>{node}</div>);

  while (position < lines.length) {
    const line = lines[position];

    if (!line.trim()) {
      position += 1;
      continue;
    }

    if (line.startsWith("```")) {
      const buffer: string[] = [];
      position += 1;
      while (position < lines.length && !lines[position].startsWith("```")) {
        buffer.push(lines[position]);
        position += 1;
      }
      position += 1;
      push(<pre className="doc__code">{buffer.join("\n")}</pre>);
      continue;
    }

    if (/^---+$/.test(line.trim())) {
      push(<hr className="doc__rule" />);
      position += 1;
      continue;
    }

    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1].length;
      const text = inline(heading[2]);
      push(
        level <= 2 ? <h2 className="doc__h2">{text}</h2> : <h3 className="doc__h3">{text}</h3>,
      );
      position += 1;
      continue;
    }

    // Tabela: wiersz nagłówka, wiersz z kreskami, potem treść.
    if (line.trim().startsWith("|") && (lines[position + 1] ?? "").includes("---")) {
      const head = cells(line);
      position += 2;
      const rows: string[][] = [];
      while (position < lines.length && lines[position].trim().startsWith("|")) {
        rows.push(cells(lines[position]));
        position += 1;
      }
      push(
        <div className="doc__tablewrap">
          <table className="doc__table">
            <thead>
              <tr>
                {head.map((cell, index) => (
                  <th key={index}>{inline(cell)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  {row.map((cell, cellIndex) => (
                    <td key={cellIndex}>{inline(cell)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (/^[-*]\s+/.test(line.trim())) {
      const items: string[] = [];
      while (position < lines.length && /^[-*]\s+/.test(lines[position].trim())) {
        items.push(lines[position].trim().replace(/^[-*]\s+/, ""));
        position += 1;
      }
      push(
        <ul className="doc__list">
          {items.map((item, index) => (
            <li key={index}>{inline(item)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    const paragraph: string[] = [];
    while (position < lines.length && lines[position].trim() && !lines[position].startsWith("#") &&
           !lines[position].trim().startsWith("|") && !lines[position].startsWith("```")) {
      paragraph.push(lines[position]);
      position += 1;
    }
    push(<p className="doc__p">{inline(paragraph.join(" "))}</p>);
  }

  return out;
}

function cells(line: string): string[] {
  return line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((cell) => cell.trim());
}

/** `kod`, **pogrubienie** — reszta zostaje tekstem. */
function inline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const pattern = /(`[^`]+`|\*\*[^*]+\*\*)/g;
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text))) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const token = match[0];
    parts.push(
      token.startsWith("`") ? (
        <code key={parts.length} className="doc__inline">
          {token.slice(1, -1)}
        </code>
      ) : (
        <strong key={parts.length}>{token.slice(2, -2)}</strong>
      ),
    );
    last = match.index + token.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
